<script setup lang="ts">

import { SancareBreadcrumb } from '@sancare/typescript-commons'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { useBreadcrumb } from '@/breadcrumbs/useBreadcrumb'
import router from '@/router'
import { AutomationGroup } from '@/store/modules/automation/types'

import AutomationGroupDetails from './AutomationGroupDetails.vue'

const store = useStore()
const { breadcrumbItems } = useBreadcrumb()

const selectedVersionId = ref<number | null>(null)
const automationGroup = computed<AutomationGroup>(() => store.state.automation.editedAutomationGroup)

watch(() => automationGroup.value?.name, (name) => {
  if (name && store.state.route.query?.staylistLabel !== name) {
    router.replace({ query: { ...store.state.route.query, staylistLabel: name } })
  }
}, { immediate: true })
const automationGroupVersions = computed<number[]>(() => store.state.automation.automationGroupVersions)
const automationGroupId = computed<number>(() => store.state.route.params.automationGroupId)
const automatedGroupDataToBeShown = computed(() => {
  if (selectedVersionId.value === null) {
    return automationGroup.value
  }

  return getVersion(selectedVersionId.value).state
})
onMounted(() => {
  store.dispatch('getAutomationGroup', automationGroupId.value)
  store.dispatch('getAutomationGroupVersions', automationGroupId.value)
})

function getVersionTitle(versionId: number | null) {
  const version = getVersion(versionId)
  if (version) {
    return `Version antérieure au ${dayjs(version.updatedAt).format('DD/MM/YYYY HH:mm:ss')}`
  }
  return 'Version courante'
}

function getVersion(id: number) {
  return automationGroupVersions.value.find((v) => v.id === id)
}

</script>

<template>
  <div class="container">
    <sancare-breadcrumb :items="breadcrumbItems" />
    <div class="stay-list-criteria card">
      <div class="card-body">
        <h1 class="mb-4">
          Historique des versions
        </h1>
        <div class="row">
          <div class="col-3">
            <div class="list-group">
              <a
                href="#"
                class="list-group-item list-group-item-action"
                :class="{active: selectedVersionId === null}"
                @click.prevent="selectedVersionId = null"
              >
                <h6>Version courante</h6>
              </a>
              <a
                v-for="version in automationGroupVersions"
                :key="version.id"
                href="#"
                class="list-group-item list-group-item-action"
                :class="{active: version.id === selectedVersionId}"
                @click.prevent="selectedVersionId = version.id"
              >
                <h6>{{ getVersionTitle(version.id) }}</h6>
                <p>Modifiée par {{ version.user.username }}</p>
              </a>
            </div>
          </div>
          <div class="col-9">
            <div class="card px-3 py-2">
              <h2 class="mb-3">
                {{ getVersionTitle(selectedVersionId) }}
              </h2>
              <div>
                Nom du groupe: <span class="badge text-bg-primary">{{ automatedGroupDataToBeShown.name }}</span>
              </div>
              <automation-group-details :full-automation-group="automatedGroupDataToBeShown" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h1 {
  font-size: 1.5rem;
}

h2 {
  font-size: 1.1rem;
}
</style>
